/**
 * SearchPop module
 * This module exports the SearchPop component, which is a modal for searching and selecting datasets.
 * It also includes the DatasetDetails component and a custom hook to manage the selected dataset.
 *
 * Dependencies:
 * - react, react-router-dom, antd, underscore, and store
 */

import { Empty, Modal } from 'antd';
import { FC } from 'react';
import useSearchStore from '../../../store/searchStore';
import { IDatasetBrief } from '../../../types/metadata';
import SearchMetadata from './SearchMetadata';
import styles from './SearchPop.module.scss';

interface PrivateProps {
  visible: boolean;
  onHandleDatasetsModalCancel: () => void;
  searchID: (id: string) => void;
}

interface DatasetDetailsProps {
  dataset: IDatasetBrief;
}

/**
 * DatasetDetails: A component to display the details of a selected dataset
 *
 * @param {Object} props - The properties passed to this component
 * @returns {ReactElement} The rendered DatasetDetails component
 */
const DatasetDetails: FC<DatasetDetailsProps> = ({ dataset }) => (
  <div className={styles.datasetContainer}>
    <p>
      <b className={styles.datasetLabel}>Title:</b>
      <span>{dataset.title}</span>
    </p>
    <p>
      <b className={styles.datasetLabel}>Acronym:</b>
      <span>{dataset.acronym}</span>
    </p>
    <p>
      <b className={styles.datasetLabel}>IsLoginRequired:</b>
      <span>{dataset.isLoginRequired}</span>
    </p>
    <p>
      <b className={styles.datasetLabel}>Description:</b>
      <span>{dataset.description}</span>
    </p>
    <div>
      <SearchMetadata options={dataset.id}></SearchMetadata>
    </div>
  </div>
);

/**
 * SearchPop: A modal component to search and select datasets
 *
 * @param {Object} props - The properties passed to this component
 * @returns {ReactElement} The rendered SearchPop component
 */
const SearchPop: FC<PrivateProps> = ({
  visible,
  onHandleDatasetsModalCancel,
  searchID,
}) => {
  const selectDataset = useSearchStore((state) => state.selectedDataset);
  const setSelectDataset = useSearchStore((state) => state.setSelectedDataset);
  const setSelectedSearchId = useSearchStore(
    (state) => state.setSelectedSearchId
  );

  function handleCancel() {
    searchID('');
    setSelectDataset(null);
    setSelectedSearchId('');
    onHandleDatasetsModalCancel();
  }

  return (
    <Modal
      title='Dataset Details'
      open={visible}
      onOk={() => {}}
      onCancel={handleCancel}
      footer={null}
      width={1000}
      zIndex={9999}
    >
      {selectDataset && <DatasetDetails dataset={selectDataset} />}
      {!selectDataset && <Empty />}
    </Modal>
  );
};

export default SearchPop;
